import { InputFieldProps } from "./InputField.types";
import {
  CharacterCountWrapper,
  CircleIcon,
  CloseIcon,
  CloseIconWrapper,
  DropdownIcon,
  DropdownSelectedValue,
  InputFieldContainer,
  InputFieldInput,
  InputFieldLabel,
  InputFieldPlaceholder,
  InputFieldSpan,
  InputFieldUtil,
  InputFieldUtilWrapper,
  InputFieldWrapper,
  SupportingText,
} from "./InputField.styles";
import {
  Body4RegularTypography,
  Body5MediumTypography,
  Caps2SemiboldTypography,
  Heading2SemiboldTypography,
} from "../../primitives/Typography/typographyVariants";
import { FlexColumn } from "../Layout/FlexColumn/FlexColumn";
import { BottomSheet } from "../BottomSheet/BottomSheet";
import { useState } from "react";
import RadioGroup from "../RadioGroup/RadioGroup";
import { Option } from "../RadioButton/RadioButton.types";
import { VerticalSpacer } from "../../primitives/Spacer/VerticalSpacer";

export const InputField = (props: InputFieldProps) => {
  const {
    value = "",
    label = "",
    variant = "default",
    onChange,
    showCrossButton = true,
    showCharacterCount = false,
    maxCharacterLimit,
    helpText = "",
    type,
    isRequired = false,
    onBlur,
    onClick,
    readOnly,
    onClickCross,
    dataCy = null,
  } = props;

  const options = type === "dropdown" ? props.options : undefined;

  const [showBottomSheet, setShowBottomSheet] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState(false);
  const clearInputValue = (e: React.MouseEvent) => {
    e.stopPropagation(); // Stop event propagation to prevent input field click

    if (onClickCross) {
      onClickCross(); // Execute the external function if it's provided
      return; // Exit the function early, preventing any further logic
    }

    // If onClickCross is not provided, clear the input value
    // @ts-ignore
    onChange({
      target: { value: "" },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value;

    if (type !== "dropdown") {
      // Remove spaces from input

      if (type === "number") {
        newValue = newValue.replace(/\s+/g, "");
      }

      if (!maxCharacterLimit || newValue.length <= maxCharacterLimit) {
        onChange &&
          onChange({ ...e, target: { ...e.target, value: newValue } });
      }
    }
  };
  const handleToggleBottomSheet = () => {
    setShowBottomSheet((prev) => !prev);
  };

  const handleWrapperClick = () => {
    if (type === "dropdown" && !!options) {
      handleToggleBottomSheet();
    } else if (onClick) {
      onClick();
    }
  };
  const handleDropdownValueChange = (option: Option) => {
    if (type === "dropdown") {
      onChange(option);
      setShowBottomSheet(false);
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (type === "number") {
      if (["e", "E", "+", "-", ".", " "].includes(e.key)) {
        e.preventDefault();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (type === "number") {
      const pasteData = e.clipboardData.getData("text");
      if (!/^\d+$/.test(pasteData)) {
        e.preventDefault();
      }
    }
  };

  const handleFocus = () => {
    if (!props.readOnly) {
      setIsFocused(true);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    onBlur && onBlur(e);
  };
  const shouldShowInputFieldUtil =
    showCharacterCount || showCrossButton || type === "dropdown";
  return (
    <InputFieldContainer>
      <InputFieldWrapper variant={variant} onClick={handleWrapperClick}>
        {(value || isFocused) && (
          <InputFieldLabel htmlFor={label}>
            <Caps2SemiboldTypography>
              {label}
              {isRequired ? <InputFieldSpan>*</InputFieldSpan> : ""}
            </Caps2SemiboldTypography>
          </InputFieldLabel>
        )}

        {label && !value && !isFocused && (
          <InputFieldPlaceholder>
            {label}
            {isRequired ? <InputFieldSpan>*</InputFieldSpan> : ""}
          </InputFieldPlaceholder>
        )}

        <InputFieldUtilWrapper>
          {type === "dropdown" ? (
            <DropdownSelectedValue value={value}>{value}</DropdownSelectedValue>
          ) : (
            <InputFieldInput
              variant={variant}
              value={value}
              onChange={handleInputChange}
              type={type}
              inputMode={type === "number" ? "numeric" : undefined}
              pattern={type === "number" ? "[0-9]*" : undefined}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              isFocused={isFocused}
              readOnly={readOnly}
              data-cy={dataCy}
            ></InputFieldInput>
          )}
          {shouldShowInputFieldUtil && (
            <InputFieldUtil>
              {showCharacterCount && (
                <CharacterCountWrapper>
                  {value.length}/{maxCharacterLimit}
                </CharacterCountWrapper>
              )}
              {props.icon && !value && type !== "dropdown" && (
                <CloseIconWrapper>
                  <span onClick={props.onClickIcon}>{props.icon}</span>
                </CloseIconWrapper>
              )}
              {showCrossButton && value && type !== "dropdown" && (
                <CloseIconWrapper>
                  <CloseIcon
                    onClick={clearInputValue}
                    className={`fa-solid fa-circle-xmark`}
                  ></CloseIcon>
                </CloseIconWrapper>
              )}
              {type === "dropdown" && (
                <DropdownIcon className="fa-solid fa-caret-down"></DropdownIcon>
              )}
            </InputFieldUtil>
          )}
        </InputFieldUtilWrapper>
      </InputFieldWrapper>
      {helpText && (
        <SupportingText variant={variant}>
          <CircleIcon
            className="fa-solid fa-circle"
            variant={variant}
          ></CircleIcon>
          <Body4RegularTypography>{helpText}</Body4RegularTypography>
        </SupportingText>
      )}
      {type === "dropdown" && !!options && (
        <BottomSheet
          open={showBottomSheet}
          handleClose={handleToggleBottomSheet}
        >
          <FlexColumn py="small">
            <Heading2SemiboldTypography fontFamily="Lexend">
              {label}
            </Heading2SemiboldTypography>
            <VerticalSpacer size="small" />
            <Body5MediumTypography>
              You would not be able to enter it later!
            </Body5MediumTypography>
          </FlexColumn>
          <VerticalSpacer size="large" />
          <FlexColumn>
            <RadioGroup
              options={options}
              value={value}
              onChange={handleDropdownValueChange}
            />
          </FlexColumn>
          <VerticalSpacer size="large" />
        </BottomSheet>
      )}
    </InputFieldContainer>
  );
};
